import { ChangeDetectionStrategy, Component, HostBinding, computed, inject, input, output } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { TranslocoPipe } from '@jsverse/transloco';

import { BaseControlValueAccessor } from '~core/abstractions/base-control-value-accessor';
import { ErrorTooltipDirective } from '~core/directives/error-tooltip.directive';
import { UnitsEnum } from '~core/models/enums/units.enum';
import { NumberInputParseOptions, NumberInputService } from '~core/services/number-input.service';
import { QuantityUnitDisplayComponent } from '../../quantity-unit-display/quantity-unit-display.component';

@Component({
    selector: 'arc-number-input',
    templateUrl: './number-input.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: NumberInputComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        MatFormField,
        ErrorTooltipDirective,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        QuantityUnitDisplayComponent,
        MatSuffix,
        TranslocoPipe
    ]
})
export class NumberInputComponent extends BaseControlValueAccessor<number> {
    @HostBinding('class') classes = 'grow min-w-0';

    readonly unit = input<string | UnitsEnum>();
    readonly unitQuantity = input<number>();
    readonly isUnitTranslated = input(false);
    readonly decimalPlaces = input<number>();
    readonly minDecimalPlaces = input<number>();
    readonly maxSuffixWidth = input(62);
    readonly min = input<number>();
    readonly max = input<number>();
    readonly isReadonly = input(false);
    readonly floatLabel = input<'always' | 'auto'>('auto');

    readonly enterKeyDown = output();
    readonly focusChanged = output<Event>();
    readonly unitText = computed(() => '');
    readonly unitTooltip = computed(() => '');

    readonly _isCurrency: boolean = false;

    private numberParseOptions = computed<NumberInputParseOptions>(() => ({
        min: this.min(),
        max: this.max(),
        decimalPlaces: this.decimalPlaces(),
        minDecimalPlaces: this.minDecimalPlaces(),
        isCurrency: this._isCurrency
    }));

    private readonly numberInputService = inject(NumberInputService);

    override writeValue(value?: number): void {
        const [roundedString, roundedNr] = this.numberInputService.parse(value, this.numberParseOptions());

        super.writeValue(roundedNr);
        this.internalControl.setValue(roundedString);
    }

    onInputChanged(): void {
        const [roundedString, roundedNr] = this.numberInputService.parse(this.internalControl.value, this.numberParseOptions());
        this.valueChanged(roundedNr);
        this.internalControl.setValue(roundedString);
    }

    onBlur(): void {
        const [roundedString, roundedNr] = this.numberInputService.parse(this.internalControl.value, this.numberParseOptions());
        if (this.internalControl.value !== roundedString) {
            this.valueChanged(roundedNr);
            this.internalControl.setValue(roundedString);
        }
    }
}
